import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/AppLayout';
import { PageHeader } from '@/components/ui/page-header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export default function VectorShiftDirectTest() {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [resumeText, setResumeText] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<any>(null);

  const handleTest = async () => {
    if (!resumeText.trim()) {
      toast({
        title: "Resume required",
        description: "Please enter resume text to test.",
        variant: "destructive"
      });
      return;
    }

    if (!jobDescription.trim()) {
      toast({
        title: "Job description required", 
        description: "Please enter job description text to test.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    setResults(null);

    try {
      console.log('=== DIRECT VECTORSHIFT TEST ===');
      console.log('Resume length:', resumeText.length);
      console.log('Job description length:', jobDescription.length);
      console.log('Additional instructions length:', additionalInstructions.length);

      const { data, error } = await supabase.functions.invoke('vectorshift-test', {
        body: {
          Resume: resumeText,
          Job_Description: jobDescription,
          Additional_Instructions: additionalInstructions || "No additional instructions provided."
        }
      });

      console.log('=== VECTORSHIFT RESPONSE ===');
      console.log('Error:', error);
      console.log('Data:', data);

      if (error) {
        throw new Error(error.message || 'VectorShift API error');
      }

      if (!data.success) {
        throw new Error(data.error || 'VectorShift processing failed');
      }

      setResults(data.data);
      toast({
        title: "VectorShift test completed!",
        description: "Check the results below."
      });

    } catch (error: any) {
      console.error('VectorShift test error:', error);
      toast({
        title: "Test failed",
        description: error.message || "Failed to call VectorShift API.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const clearAll = () => {
    setResumeText('');
    setJobDescription('');
    setAdditionalInstructions('');
    setResults(null);
  };

  return (
    <AppLayout currentPage="vectorshift-test">
      <div className="min-h-screen bg-background">
        <PageHeader 
          title="VectorShift Direct Test" 
          description="Test VectorShift pipelines directly with custom inputs"
        >
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </PageHeader>

        <div className="p-6">
          <div className="max-w-6xl mx-auto space-y-6">
            
            {/* Input Form */}
            <Card>
              <CardHeader>
                <CardTitle>VectorShift Pipeline Test</CardTitle>
                <CardDescription>
                  Pipeline 1: 689e1692c31731d850683667 (Tailoring) | 
                  Pipeline 2: 689d6717c31731d8504a49a4 (Analysis)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                
                <div className="space-y-2">
                  <Label htmlFor="resume">Resume Text *</Label>
                  <Textarea
                    id="resume"
                    placeholder="Paste your resume text here..."
                    value={resumeText}
                    onChange={(e) => setResumeText(e.target.value)}
                    rows={8}
                    className="min-h-[200px]"
                  />
                  <p className="text-sm text-muted-foreground">
                    Characters: {resumeText.length}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="job">Job Description *</Label>
                  <Textarea
                    id="job"
                    placeholder="Paste job description here..."
                    value={jobDescription}
                    onChange={(e) => setJobDescription(e.target.value)}
                    rows={6}
                    className="min-h-[150px]"
                  />
                  <p className="text-sm text-muted-foreground">
                    Characters: {jobDescription.length}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="instructions">Additional Instructions (Optional)</Label>
                  <Textarea
                    id="instructions"
                    placeholder="Any additional tailoring instructions..."
                    value={additionalInstructions}
                    onChange={(e) => setAdditionalInstructions(e.target.value)}
                    rows={4}
                  />
                  <p className="text-sm text-muted-foreground">
                    Characters: {additionalInstructions.length}
                  </p>
                </div>

                <div className="flex gap-4">
                  <Button 
                    onClick={handleTest} 
                    disabled={isLoading}
                    className="flex-1"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Testing VectorShift...
                      </>
                    ) : (
                      'Test VectorShift Pipelines'
                    )}
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    onClick={clearAll}
                    disabled={isLoading}
                  >
                    Clear All
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Results Display */}
            {results && (
              <Card>
                <CardHeader>
                  <CardTitle>VectorShift Results</CardTitle>
                  <CardDescription>Raw output from VectorShift pipelines</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    
                    {/* Main Results */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      
                      {results.Final_Resume_Text && (
                        <div className="space-y-2">
                          <Label className="font-semibold">Tailored Resume</Label>
                          <Textarea
                            value={results.Final_Resume_Text}
                            readOnly
                            rows={10}
                            className="font-mono text-sm"
                          />
                          <p className="text-xs text-muted-foreground">
                            Length: {results.Final_Resume_Text.length} characters
                          </p>
                        </div>
                      )}

                      {results.Cover_Letter && (
                        <div className="space-y-2">
                          <Label className="font-semibold">Cover Letter</Label>
                          <Textarea
                            value={results.Cover_Letter}
                            readOnly
                            rows={10}
                            className="font-mono text-sm"
                          />
                          <p className="text-xs text-muted-foreground">
                            Length: {results.Cover_Letter.length} characters
                          </p>
                        </div>
                      )}

                    </div>

                    {/* Additional Documents */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      
                      {results.Thank_You_Letter && (
                        <div className="space-y-2">
                          <Label className="font-semibold">Thank You Letter</Label>
                          <Textarea
                            value={results.Thank_You_Letter}
                            readOnly
                            rows={6}
                            className="font-mono text-sm"
                          />
                        </div>
                      )}

                      {results.Executive_Bio && (
                        <div className="space-y-2">
                          <Label className="font-semibold">Executive Bio</Label>
                          <Textarea
                            value={results.Executive_Bio}
                            readOnly
                            rows={6}
                            className="font-mono text-sm"
                          />
                        </div>
                      )}

                      {results.Linkedin_Profile && (
                        <div className="space-y-2">
                          <Label className="font-semibold">LinkedIn Profile</Label>
                          <Textarea
                            value={results.Linkedin_Profile}
                            readOnly
                            rows={6}
                            className="font-mono text-sm"
                          />
                        </div>
                      )}

                    </div>

                    {/* Analysis Results */}
                    {(results.original_analysis || results.tailored_analysis) && (
                      <div className="space-y-4">
                        <Label className="font-semibold text-lg">Analysis Results</Label>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {results.original_analysis && (
                            <div className="space-y-2">
                              <Label className="font-semibold">Original Resume Analysis</Label>
                              <div className="p-4 bg-muted rounded-lg">
                                <p className="text-sm">
                                  <strong>Overall Score:</strong> {results.original_analysis.overallScore}%
                                </p>
                                {results.original_analysis.detailedScores && (
                                  <div className="mt-2 space-y-1">
                                    <p className="text-xs"><strong>ATS:</strong> {results.original_analysis.detailedScores.atsbestPractices}%</p>
                                    <p className="text-xs"><strong>Keywords:</strong> {results.original_analysis.detailedScores.keywordsSkills}%</p>
                                    <p className="text-xs"><strong>Experience:</strong> {results.original_analysis.detailedScores.experienceAbilities}%</p>
                                    <p className="text-xs"><strong>Requirements:</strong> {results.original_analysis.detailedScores.requirementsQualifications}%</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {results.tailored_analysis && (
                            <div className="space-y-2">
                              <Label className="font-semibold">Tailored Resume Analysis</Label>
                              <div className="p-4 bg-muted rounded-lg">
                                <p className="text-sm">
                                  <strong>Overall Score:</strong> {results.tailored_analysis.overallScore}%
                                </p>
                                {results.tailored_analysis.detailedScores && (
                                  <div className="mt-2 space-y-1">
                                    <p className="text-xs"><strong>ATS:</strong> {results.tailored_analysis.detailedScores.atsbestPractices}%</p>
                                    <p className="text-xs"><strong>Keywords:</strong> {results.tailored_analysis.detailedScores.keywordsSkills}%</p>
                                    <p className="text-xs"><strong>Experience:</strong> {results.tailored_analysis.detailedScores.experienceAbilities}%</p>
                                    <p className="text-xs"><strong>Requirements:</strong> {results.tailored_analysis.detailedScores.requirementsQualifications}%</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Meta Information */}
                    <div className="space-y-2">
                      <Label className="font-semibold">Meta Information</Label>
                      <div className="p-4 bg-muted rounded-lg text-sm">
                        {results.company_name && <p><strong>Company:</strong> {results.company_name}</p>}
                        {results.job_title && <p><strong>Job Title:</strong> {results.job_title}</p>}
                      </div>
                    </div>

                    {/* Raw JSON Output */}
                    <div className="space-y-2">
                      <Label className="font-semibold">Raw JSON Output</Label>
                      <Textarea
                        value={JSON.stringify(results, null, 2)}
                        readOnly
                        rows={15}
                        className="font-mono text-xs"
                      />
                    </div>

                  </div>
                </CardContent>
              </Card>
            )}

          </div>
        </div>
      </div>
    </AppLayout>
  );
}